import { z } from "zod"

/**
 * PLACEHOLDER until the real product data arrives. Names and groups follow
 * the sales tool the advisors know; the pension per contribution is not a
 * tariff calculation, see `rechenweg` and `LOESUNG_ANNAHMEN`. Swapping the
 * catalog, those assumptions and that function is all the real data should
 * need.
 *
 * Keys are stored in run state, so existing ones keep their meaning:
 * `aktivplan_einmal` and `aktivplan_basis` stay the 100 % fund variants.
 * `planx_direkt` was dropped after tester feedback; `loesungBilanz` skips it
 * in older runs instead of failing on it.
 */
export const LOESUNG_PRODUKTE = [
  {
    key: "aktivplan_klassik_einmal",
    gruppe: "Geldrente · Einmalbeitrag",
    label: "Aktivplan Klassik (0 % Fonds)",
    art: "einmal",
    fondsanteil: 0,
  },
  {
    key: "aktivplan_5050_einmal",
    gruppe: "Geldrente · Einmalbeitrag",
    label: "Aktivplan 50/50 (50 % Fonds)",
    art: "einmal",
    fondsanteil: 0.5,
  },
  {
    key: "aktivplan_einmal",
    gruppe: "Geldrente · Einmalbeitrag",
    label: "Aktivplan Chance (100 % Fonds)",
    art: "einmal",
    fondsanteil: 1,
  },
  {
    key: "aktivplan_klassik",
    gruppe: "Geldrente · Ratierlich",
    label: "Aktivplan Klassik (0 % Fonds)",
    art: "laufend",
    fondsanteil: 0,
  },
  {
    key: "aktivplan_5050",
    gruppe: "Geldrente · Ratierlich",
    label: "Aktivplan 50/50 (50 % Fonds)",
    art: "laufend",
    fondsanteil: 0.5,
  },
  {
    key: "aktivplan_basis",
    gruppe: "Geldrente · Ratierlich",
    label: "Aktivplan Chance (100 % Fonds)",
    art: "laufend",
    fondsanteil: 1,
  },
  {
    key: "bav_direkt",
    gruppe: "Betriebliche Altersversorgung",
    label: "bAV-Direktversicherung",
    art: "laufend",
    // No tax and social security advantage yet: it needs the real bAV logic,
    // a flat factor would claim more precision than this placeholder has.
    fondsanteil: 0.5,
  },
  {
    key: "lbs_immobilie",
    gruppe: "Immobilien-Rente · mit LBS-Bausparen",
    label: "Immobilie mit LBS-Bausparen",
    art: "laufend",
    fondsanteil: 0,
    // Testers rated it secondary for the consultation; kept, but last and
    // marked as a supplement.
    ergaenzend: true,
  },
] as const satisfies readonly {
  key: string
  gruppe: string
  label: string
  art: "einmal" | "laufend"
  fondsanteil: number
  ergaenzend?: boolean
}[]

export type LoesungProdukt = (typeof LOESUNG_PRODUKTE)[number]
export type LoesungProduktKey = LoesungProdukt["key"]

export const produktKeySchema = z.enum(
  LOESUNG_PRODUKTE.map((p) => p.key) as [
    LoesungProduktKey,
    ...LoesungProduktKey[],
  ]
)

export const loesungVorschlagSchema = z.object({
  produkt: produktKeySchema,
  /** Monthly for `laufend`, one-off for `einmal`. */
  beitrag: z.number().min(0).max(10_000_000),
})
export type LoesungVorschlag = z.infer<typeof loesungVorschlagSchema>

export function loesungProdukt(key: LoesungProduktKey): LoesungProdukt {
  return LOESUNG_PRODUKTE.find((p) => p.key === key)!
}

export function istLoesungProdukt(key: string): key is LoesungProduktKey {
  return LOESUNG_PRODUKTE.some((p) => p.key === key)
}
